<!-- TEMPORARY - scanner engine benchmark, remove after arbitration -->
<template>
    <div id="scanner-benchmark">
        <scanner-benchmark-camera-component
            v-if="active_engine"
            :camera_profile="camera_profile"
            :engine="active_engine"
            v-on:cancel="closeScanner"
            v-on:finished="handleFinished"
        />

        <v-container class="benchmark-shell">
            <div class="benchmark-header mb-6">
                <v-icon class="mr-4" color="white" large>mdi-barcode-scan</v-icon>
                <div>
                    <p class="benchmark-header-title mb-1">Comparatif des moteurs de scan</p>
                    <p class="benchmark-header-subtitle mb-0">
                        Choisissez un moteur, scannez une étiquette, comparez. Les résultats restent sur cet appareil.
                    </p>
                </div>
            </div>

            <p class="benchmark-section-label">Profil caméra</p>
            <v-btn-toggle v-model="camera_profile" class="benchmark-toggle mb-2" color="primary" mandatory>
                <v-btn v-for="(profile, key) in camera_profiles" :key="key" :value="key" class="flex-grow-1" small>
                    {{ profile.label }}
                </v-btn>
            </v-btn-toggle>
            <p class="benchmark-hint mb-8">{{ camera_profiles[camera_profile].description }}</p>

            <p class="benchmark-section-label">Formats recherchés</p>
            <v-btn-toggle v-model="format_mode" class="benchmark-toggle mb-2" color="primary" mandatory>
                <v-btn v-for="(mode, key) in format_modes" :key="key" :value="key" class="flex-grow-1" small>
                    {{ mode.label }}
                </v-btn>
            </v-btn-toggle>
            <p class="benchmark-hint mb-8">{{ format_modes[format_mode].description }}</p>

            <p class="benchmark-section-label">Valeur attendue</p>
            <v-text-field
                v-model="expected_value"
                background-color="white"
                clearable
                dense
                hide-details
                outlined
                placeholder="Collez le code exact imprimé sur l’étiquette"
                prepend-inner-icon="mdi-check-decagram-outline"
            />
            <p class="benchmark-hint mb-8">
                Facultatif. Si renseignée, une valeur décodée différente est signalée comme mauvaise lecture au lieu d’un succès &mdash; une
                seule mauvaise lecture disqualifie un moteur, aussi rapide soit-il.
            </p>

            <p class="benchmark-section-label">Moteurs</p>
            <v-card
                v-for="row in engine_rows"
                :key="row.id"
                :class="{ 'is-disabled': row.support !== 'supported' }"
                class="benchmark-engine mb-3"
                flat
                @click="selectEngine(row)"
            >
                <div class="benchmark-engine-icon" :class="`is-${row.support}`">
                    <v-icon :color="row.support === 'supported' ? '#11688f' : '#9aa7b5'">mdi-chip</v-icon>
                </div>

                <div class="benchmark-engine-body">
                    <p class="benchmark-engine-title mb-1">{{ row.label }}</p>
                    <p class="benchmark-hint mb-0">{{ row.source }}</p>
                    <div class="mt-2">
                        <v-chip class="font-12" color="grey" label outlined small>
                            {{ row.license }}
                        </v-chip>
                    </div>
                    <p v-if="row.support === 'unsupported'" class="benchmark-hint error--text mt-2 mb-0">
                        {{ row.unsupported_reason }}
                    </p>
                </div>

                <div class="benchmark-engine-status">
                    <v-progress-circular v-if="row.loading" :size="22" :width="2" color="#1ba0db" indeterminate />
                    <v-chip v-else :color="supportColor(row)" class="font-12" dark small>{{ supportLabel(row) }}</v-chip>
                </div>
            </v-card>

            <div class="d-flex justify-space-between align-center mt-8 mb-3">
                <p class="benchmark-section-label mb-0">Résultats &middot; {{ results.length }}</p>
                <div>
                    <v-btn :disabled="!results.length" class="mr-1" small text @click="copyResults">
                        <v-icon left small>mdi-content-copy</v-icon>
                        Copier
                    </v-btn>
                    <v-btn :disabled="!results.length" color="error" small text @click="clearResults">
                        <v-icon left small>mdi-delete-outline</v-icon>
                        Effacer
                    </v-btn>
                </div>
            </div>

            <div v-if="!results.length" class="benchmark-empty">
                <v-icon color="#c3ccd6" large>mdi-clipboard-text-clock-outline</v-icon>
                <p class="benchmark-hint mt-2 mb-0">Aucun test enregistré pour le moment.</p>
            </div>

            <v-card v-for="(result, index) in results" :key="index" :class="`is-${result.outcome}`" class="benchmark-result mb-3" flat>
                <div class="d-flex justify-space-between align-center mb-2">
                    <span class="benchmark-engine-title">{{ result.engine_label }}</span>
                    <v-chip :color="outcomeColor(result.outcome)" class="font-12" dark small>
                        <v-icon left small>{{ outcomeIcon(result.outcome) }}</v-icon>
                        {{ outcomeLabel(result.outcome) }}
                    </v-chip>
                </div>

                <p v-if="result.decoded_text" class="benchmark-result-value mb-2">
                    {{ result.decoded_text }}
                    <span class="benchmark-hint">({{ result.decoded_format }})</span>
                </p>

                <div class="benchmark-metrics">
                    <div><span class="benchmark-metric-key">Symbologie</span>{{ result.decoded_format || '-' }}</div>
                    <div><span class="benchmark-metric-key">Profil</span>{{ result.camera_profile }}</div>
                    <div><span class="benchmark-metric-key">Formats</span>{{ result.format_mode }}</div>
                    <div><span class="benchmark-metric-key">Flux</span>{{ result.resolution }}</div>
                    <div><span class="benchmark-metric-key">ROI</span>{{ result.roi }}</div>
                    <div>
                        <span class="benchmark-metric-key">Temps</span>
                        <template v-if="result.ttfr_ms !== null">{{ result.ttfr_ms }} ms</template>
                        <template v-else>abandon à {{ result.elapsed_ms }} ms</template>
                    </div>
                    <div><span class="benchmark-metric-key">Tentatives</span>{{ result.attempts }}</div>
                    <div><span class="benchmark-metric-key">Décodage</span>{{ result.decode_ms_median }} ms médian</div>
                    <div v-if="result.decode_errors"><span class="benchmark-metric-key">Erreurs</span>{{ result.decode_errors }}</div>
                </div>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import ScannerBenchmarkCameraComponent, { CAMERA_PROFILES } from '../../components/benchmark/ScannerBenchmarkCameraComponent.vue'
import scannerEngines, { FORMAT_MODES, getFormatMode, setFormatMode } from '../../components/benchmark/scannerEngines'

const STORAGE_KEY = 'scanner_benchmark_results_v1'
const MAX_STORED_RESULTS = 200
const OUTCOME_LABELS = {
    success: 'Conforme',
    success_unverified: 'Lu',
    mismatch: 'Mauvaise lecture',
    no_read: 'Aucune lecture',
}

const OUTCOME_ICONS = {
    success: 'mdi-check-circle-outline',
    success_unverified: 'mdi-help-circle-outline',
    mismatch: 'mdi-alert-circle-outline',
    no_read: 'mdi-close-circle-outline',
}

export default {
    name: 'ScannerBenchmark',
    components: { ScannerBenchmarkCameraComponent },
    data() {
        return {
            camera_profiles: CAMERA_PROFILES,
            camera_profile: 'hq_roi',
            format_modes: FORMAT_MODES,
            format_mode: getFormatMode(),
            expected_value: '',
            selected_engine_id: '',
            engine_rows: scannerEngines.map((engine) => ({
                id: engine.id,
                label: engine.label,
                license: engine.license,
                source: engine.source,
                support: 'checking',
                unsupported_reason: '',
                ready: false,
                loading: false,
                load_ms: 0,
            })),
            results: [],
        }
    },
    watch: {
        format_mode(mode) {
            setFormatMode(mode)
        },
    },
    computed: {
        active_engine() {
            return this.selected_engine_id ? this.findEngine(this.selected_engine_id) : null
        },
    },
    created() {
        // Kept off `data`: the engines hold wasm instances that Vue has no reason to make reactive.
        this.scanner_engines = scannerEngines
        this.restoreResults()
        this.checkSupport()
    },
    methods: {
        findEngine(engine_id) {
            return this.scanner_engines.find((engine) => engine.id === engine_id)
        },

        async checkSupport() {
            for (const row of this.engine_rows) {
                const engine = this.findEngine(row.id)

                try {
                    const supported = await engine.isSupported()
                    row.support = supported ? 'supported' : 'unsupported'
                    row.unsupported_reason = engine.unsupported_reason
                } catch (error) {
                    row.support = 'unsupported'
                    row.unsupported_reason = error.message
                }
            }
        },

        async selectEngine(row) {
            if (row.support !== 'supported' || row.loading) {
                return
            }

            // Scanning before the engine is ready would time the CDN download instead of the decoder.
            if (!row.ready) {
                const started_at = performance.now()
                row.loading = true

                try {
                    await this.findEngine(row.id).load()
                    row.load_ms = Math.round(performance.now() - started_at)
                    row.ready = true
                } catch (error) {
                    this.$store.commit('snackbar/showSnackbar', { color: 'error', text: `${row.label}: ${error.message}` })
                    return
                } finally {
                    row.loading = false
                }
            }

            this.selected_engine_id = row.id
        },

        handleFinished(result) {
            const row = this.engine_rows.find((engine_row) => engine_row.id === result.engine_id)

            const record = Object.assign({}, result, {
                outcome: this.resolveOutcome(result),
                expected_value: this.expected_value || '',
                format_mode: this.format_mode,
                engine_load_ms: row ? row.load_ms : 0,
                recorded_at: new Date().toISOString(),
                user_agent: navigator.userAgent,
            })

            this.results.unshift(record)
            this.persistResults()
            this.closeScanner()

            const outcome_label = this.outcomeLabel(record.outcome)

            this.$store.commit('snackbar/showSnackbar', {
                color: this.outcomeColor(record.outcome),
                text: record.decoded_text
                    ? `${record.engine_label} · ${outcome_label} — ${record.decoded_text}`
                    : `${record.engine_label} · ${outcome_label}`,
            })
        },

        resolveOutcome(result) {
            if (result.outcome !== 'success') {
                return result.outcome
            }

            if (!this.expected_value) {
                return 'success_unverified'
            }

            return result.decoded_text === this.expected_value ? 'success' : 'mismatch'
        },

        closeScanner() {
            this.selected_engine_id = ''
        },

        supportColor(row) {
            if (row.support === 'supported') {
                return row.ready ? 'green darken-1' : 'blue darken-1'
            }

            return row.support === 'checking' ? 'grey' : 'red lighten-1'
        },

        supportLabel(row) {
            if (row.support !== 'supported') {
                return row.support === 'checking' ? 'vérification' : 'non supporté'
            }

            return row.ready ? `prêt ${row.load_ms} ms` : 'charger'
        },

        outcomeColor(outcome) {
            if (outcome === 'success') {
                return 'success'
            }

            return outcome === 'success_unverified' ? 'warning' : 'error'
        },

        outcomeLabel(outcome) {
            return OUTCOME_LABELS[outcome] || outcome
        },

        outcomeIcon(outcome) {
            return OUTCOME_ICONS[outcome] || 'mdi-information-outline'
        },

        copyResults() {
            this.copy(JSON.stringify(this.results, null, 2))
        },

        clearResults() {
            this.results = []
            this.persistResults()
        },

        persistResults() {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.results.slice(0, MAX_STORED_RESULTS)))
            } catch (error) {
                // Private browsing or a full quota: the results simply stay in memory for this session.
            }
        },

        restoreResults() {
            try {
                this.results = JSON.parse(localStorage.getItem(STORAGE_KEY)) || []
            } catch (error) {
                this.results = []
            }
        },
    },
}
</script>

<style scoped>
/* The page is used on a phone in the field; the max-width only keeps it readable when opened on a desktop. */
.benchmark-shell {
    max-width: 640px;
    padding-bottom: 48px;
}

.benchmark-header {
    display: flex;
    align-items: center;
    padding: 18px 20px;
    border-radius: 10px;
    background: linear-gradient(to right, #1ba0db, #11688f);
}

.benchmark-header-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
}

.benchmark-header-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
    line-height: 16px;
}

.benchmark-section-label {
    color: #4c545f;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 8px;
}

.benchmark-hint {
    color: #788a9e;
    font-size: 12px;
    line-height: 16px;
}

.benchmark-toggle {
    width: 100%;
}

/* Vuetify uppercases and letter-spaces button labels, which overflows a two-option toggle on a 375px phone. */
.benchmark-toggle >>> .v-btn {
    letter-spacing: 0;
    font-size: 12px;
}

.benchmark-engine {
    display: flex;
    align-items: flex-start;
    padding: 14px;
    border: 1px solid #e2e8ef;
    border-radius: 10px;
}

.benchmark-engine.is-disabled {
    background-color: #f7f9fb;
}

.benchmark-engine-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    margin-right: 12px;
    border-radius: 8px;
    background-color: #eaf4fa;
}

.benchmark-engine-icon.is-unsupported {
    background-color: #eef1f4;
}

.benchmark-engine-body {
    flex-grow: 1;
    min-width: 0;
}

.benchmark-engine-title {
    color: #4c545f;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
}

.benchmark-engine-status {
    flex-shrink: 0;
    margin-left: 10px;
}

.benchmark-empty {
    padding: 28px 0;
    text-align: center;
}

.benchmark-result {
    padding: 14px;
    border: 1px solid #e2e8ef;
    border-left-width: 4px;
    border-radius: 10px;
}

.benchmark-result.is-success {
    border-left-color: #4caf50;
}

.benchmark-result.is-success_unverified {
    border-left-color: #fb8c00;
}

.benchmark-result.is-mismatch,
.benchmark-result.is-no_read {
    border-left-color: #ff5252;
}

.benchmark-result-value {
    color: #4c545f;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
}

.benchmark-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 4px 12px;
    color: #4c545f;
    font-size: 12px;
}

.benchmark-metric-key {
    display: block;
    color: #9aa7b5;
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
</style>
